/**
 * Shared color constants for all map layers — ported from WorldMonitor's
 * getOverlayColors(), MARKER_ICONS, ICON_MAPPING, ALTITUDE_COLOR_STOPS,
 * and LAYER_ZOOM_THRESHOLDS (dark-mode values only).
 *
 * MIT License — see THIRD-PARTY-NOTICES.md
 *
 * All colors defined as RGBA arrays for deck.gl compatibility.
 * Imported by both flat map layers and globe marker builders.
 */

// ── Severity Colors ──────────────────────────────────────────────────

export const SEVERITY_COLORS = {
  critical: [255, 32, 32] as [number, number, number],
  high: [255, 102, 0] as [number, number, number],
  medium: [255, 170, 0] as [number, number, number],
  low: [136, 255, 68] as [number, number, number],
  info: [68, 136, 255] as [number, number, number],
};

// ── Hotspot Escalation (5-tier) ──────────────────────────────────────

export const HOTSPOT_COLORS: Record<number, [number, number, number]> = {
  5: [255, 32, 32],    // #ff2020 — critical
  4: [255, 102, 0],    // #ff6600 — high
  3: [255, 170, 0],    // #ffaa00 — elevated
  2: [255, 221, 0],    // #ffdd00 — moderate
  1: [136, 255, 68],   // #88ff44 — low
};

export const HOTSPOT_COLORS_HEX: Record<number, string> = {
  5: '#ff2020',
  4: '#ff6600',
  3: '#ffaa00',
  2: '#ffdd00',
  1: '#88ff44',
};

// ── Military Base Faction Colors ─────────────────────────────────────

export const MILITARY_BASE_COLORS: Record<string, [number, number, number]> = {
  'us-nato': [68, 136, 255],
  russia: [255, 68, 68],
  china: [255, 136, 68],
  uk: [68, 170, 255],
  france: [0, 85, 164],
  india: [255, 153, 51],
  japan: [188, 0, 45],
  other: [136, 136, 136],
};

export const MILITARY_BASE_COLORS_HEX: Record<string, string> = {
  'us-nato': '#4488ff',
  russia: '#ff4444',
  china: '#ff8844',
  uk: '#44aaff',
  france: '#0055a4',
  india: '#ff9933',
  japan: '#bc002d',
  other: '#888888',
};

// ── Satellite Country Colors ─────────────────────────────────────────

export const SATELLITE_COLORS: Record<string, [number, number, number]> = {
  CN: [255, 32, 32],
  RU: [255, 136, 0],
  US: [68, 136, 255],
  EU: [68, 204, 68],
  KR: [170, 102, 255],
  IN: [255, 102, 170],
  TR: [255, 68, 102],
  OTHER: [204, 204, 255],
};

export const SATELLITE_COLORS_HEX: Record<string, string> = {
  CN: '#ff2020',
  RU: '#ff8800',
  US: '#4488ff',
  EU: '#44cc44',
  KR: '#aa66ff',
  IN: '#ff66aa',
  TR: '#ff4466',
  OTHER: '#ccccff',
};

// ── Overlay Layer Colors (WM dark-mode exact values) ─────────────────

export const OVERLAY_COLORS = {
  // Threat dots — identical in both WM modes
  hotspotHigh:      [255, 68, 68, 200] as [number, number, number, number],
  hotspotElevated:  [255, 165, 0, 200] as [number, number, number, number],
  hotspotLow:       [255, 255, 0, 180] as [number, number, number, number],

  // Conflict zone fills
  conflict:         [255, 0, 0, 100] as [number, number, number, number],

  // Infrastructure/category markers (dark-mode values)
  base:             [0, 150, 255, 200] as [number, number, number, number],
  nuclear:          [255, 215, 0, 200] as [number, number, number, number],
  datacenter:       [136, 68, 255, 140] as [number, number, number, number],  // Purple — WM createDatacentersLayer() hardcodes this
  cable:            [0, 200, 255, 150] as [number, number, number, number],
  cableHighlight:   [255, 100, 100, 200] as [number, number, number, number],
  cableFault:       [255, 50, 50, 220] as [number, number, number, number],
  cableDegraded:    [255, 165, 0, 200] as [number, number, number, number],
  earthquake:       [255, 100, 50, 200] as [number, number, number, number],
  vesselMilitary:   [255, 100, 100, 220] as [number, number, number, number],
  flightMilitary:   [255, 50, 50, 220] as [number, number, number, number],
  protest:          [255, 150, 0, 200] as [number, number, number, number],
  outage:           [255, 50, 50, 180] as [number, number, number, number],
  trafficAnomaly:   [255, 160, 0, 200] as [number, number, number, number],
  ddosHit:          [180, 0, 255, 200] as [number, number, number, number],
  fire:             [255, 100, 0, 200] as [number, number, number, number],
  weather:          [100, 150, 255, 180] as [number, number, number, number],
  radiation:        [255, 255, 0, 200] as [number, number, number, number],
  cyber:            [0, 255, 200, 180] as [number, number, number, number],
  displacement:     [255, 158, 11, 180] as [number, number, number, number],
  darkShip:         [255, 50, 50, 200] as [number, number, number, number],

  // Financial/economic layers (WM dark-mode)
  stockExchange:    [80, 200, 255, 210] as [number, number, number, number],
  financialCenter:  [0, 220, 150, 200] as [number, number, number, number],
  centralBank:      [255, 210, 80, 210] as [number, number, number, number],
  commodityHub:     [255, 150, 80, 200] as [number, number, number, number],

  // UCDP conflict classification
  ucdpStateBased:   [255, 50, 50, 200] as [number, number, number, number],
  ucdpNonState:     [255, 165, 0, 200] as [number, number, number, number],
  ucdpOneSided:     [255, 255, 0, 200] as [number, number, number, number],
} as const;

// ── Trade Route Arc Colors ───────────────────────────────────────────

export const TRADE_ROUTE_COLORS = {
  disrupted: {
    source: [255, 80, 80, 200] as [number, number, number, number],
    target: [255, 80, 80, 200] as [number, number, number, number],
  },
  highRisk: {
    source: [255, 180, 50, 180] as [number, number, number, number],
    target: [255, 180, 50, 180] as [number, number, number, number],
  },
  energy: {
    source: [255, 180, 50, 180] as [number, number, number, number],
    target: [255, 180, 50, 180] as [number, number, number, number],
  },
  container: {
    source: [100, 200, 255, 160] as [number, number, number, number],
    target: [100, 200, 255, 160] as [number, number, number, number],
  },
  default: {
    source: [100, 200, 255, 160] as [number, number, number, number],
    target: [100, 200, 255, 160] as [number, number, number, number],
  },
};

// ── Earthquake Magnitude Colors ──────────────────────────────────────

export function getEarthquakeColor(magnitude: number): [number, number, number] {
  if (magnitude >= 6) return [255, 0, 0];       // red
  if (magnitude >= 5) return [255, 100, 0];     // orange
  return [255, 100, 50];                         // orange-red
}

// ── Conflict Zone Colors ─────────────────────────────────────────────

export const CONFLICT_ZONE_COLORS = {
  high: { fill: [255, 0, 0, 100], stroke: [255, 0, 0, 180] },
  medium: { fill: [255, 0, 0, 100], stroke: [255, 0, 0, 180] },
  low: { fill: [255, 0, 0, 100], stroke: [255, 0, 0, 180] },
} as const;

// ── CII Choropleth Colors ────────────────────────────────────────────

export const CII_COLORS = {
  low: [40, 180, 60, 130] as [number, number, number, number],
  normal: [220, 200, 50, 135] as [number, number, number, number],
  elevated: [240, 140, 30, 145] as [number, number, number, number],
  high: [220, 50, 20, 155] as [number, number, number, number],
  critical: [140, 10, 0, 170] as [number, number, number, number],
};

// ── Path Colors ──────────────────────────────────────────────────────

export const PATH_COLORS = {
  cableFault: '#ff3030',
  cableDegraded: '#ff8800',
  cableNormal: 'rgba(0,200,255,0.65)',
  pipelineOil: 'rgba(255,140,0,0.6)',
  pipelineGas: 'rgba(80,220,120,0.6)',
  stormTrack: 'rgba(255,100,100,0.8)',
};

// ── Icon Layer Colors (per-layer flat overrides) ─────────────────────

export const ICON_LAYER_COLORS: Record<string, [number, number, number]> = {
  'military-bases': [68, 136, 255],
  nuclear: [255, 215, 0],
  airports: [160, 100, 255],
  ports: [0, 200, 255],
  'ai-datacenters': [136, 68, 255],   // Purple — matches WM datacenter layer
  spaceports: [200, 100, 255],
  conflicts: [255, 68, 68],
  'cyber-incidents': [0, 255, 200],
  protests: [255, 150, 0],
  embassies: [168, 85, 247],
  'power-plants': [255, 210, 80],
  'telecom-towers': [100, 180, 255],
};

// ── Point Layer Color Overrides ──────────────────────────────────────

export const POINT_LAYER_COLORS: Record<string, [number, number, number]> = {
  protests: [255, 150, 0],
  'protests-cluster': [255, 150, 0],
  'power-plants': [255, 210, 80],
  'telecom-towers': [100, 180, 255],
};

// ── Zoom-Dependent Progressive Disclosure (WM exact) ─────────────────

export const LAYER_ZOOM_THRESHOLDS: Record<string, { minZoom: number; showLabels?: number }> = {
  'military-bases': { minZoom: 3, showLabels: 5 },
  nuclear: { minZoom: 3 },
  conflicts: { minZoom: 1, showLabels: 3 },
  economic: { minZoom: 3 },
  natural: { minZoom: 1, showLabels: 2 },
  'ai-datacenters': { minZoom: 5 },
  irradiators: { minZoom: 4 },
  spaceports: { minZoom: 3 },
  'power-plants': { minZoom: 5 },
  'telecom-towers': { minZoom: 6 },
  embassies: { minZoom: 5 },
  ports: { minZoom: 4 },
  airports: { minZoom: 4 },
};

// ── SVG Icon Shapes (24x24 viewBox path data — for createSvgIcon()) ──

/** SVG path data for marker icon shapes (24x24 viewBox). */
export const SVG_ICON_PATHS: Record<string, string> = {
  triangle: 'M12 3L2 21h20L12 3z',
  hexagon: 'M12 2L4.27 7v10L12 22l7.73-5V7L12 2z',
  diamond: 'M12 2L4 12l8 10 8-10L12 2z',
  square: 'M4 4h16v16H4V4z',
  circle: 'M12 2a10 10 0 100 20 10 10 0 000-20z',
  star: 'M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z',
  plane: 'M21 16v-2l-8-5V3.5A1.5 1.5 0 0011.5 2 1.5 1.5 0 0010 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z',
};

// ── WM Marker Icons (32x32 viewBox, base64 data URLs for deck.gl IconLayer) ──

/**
 * Pre-built SVG data URLs for deck.gl IconLayer iconAtlas.
 * These are white-filled shapes with mask:true — deck.gl tints them via getColor.
 * Ported from WM's MARKER_ICONS.
 */
export const MARKER_ICONS = {
  square:     'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><rect x="2" y="2" width="28" height="28" rx="3" fill="white"/></svg>'),
  diamond:    'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="16,2 30,16 16,30 2,16" fill="white"/></svg>'),
  triangleUp: 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="16,2 30,28 2,28" fill="white"/></svg>'),
  hexagon:    'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="16,2 28,9 28,23 16,30 4,23 4,9" fill="white"/></svg>'),
  circle:     'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="white"/></svg>'),
  star:       'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="16,2 20,12 30,12 22,19 25,30 16,23 7,30 10,19 2,12 12,12" fill="white"/></svg>'),
  plane:      'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path d="M16 2 L17.5 10 L17 12 L27 17 L27 19 L17 16 L17 24 L20 26.5 L20 28 L16 27 L12 28 L12 26.5 L15 24 L15 16 L5 19 L5 17 L15 12 L14.5 10 Z" fill="white"/></svg>'),
} as const;

// ── Icon Mapping Objects (for deck.gl IconLayer iconMapping) ─────────

export const BASES_ICON_MAPPING = { triangleUp: { x: 0, y: 0, width: 32, height: 32, mask: true } } as const;
export const NUCLEAR_ICON_MAPPING = { hexagon: { x: 0, y: 0, width: 32, height: 32, mask: true } } as const;
export const DATACENTER_ICON_MAPPING = { square: { x: 0, y: 0, width: 32, height: 32, mask: true } } as const;
export const AIRCRAFT_ICON_MAPPING = { plane: { x: 0, y: 0, width: 32, height: 32, mask: true } } as const;

// ── Altitude Color Gradient (WM exact — Wingbits color scheme) ───────

/**
 * Altitude-based color gradient: cyan (sea level) -> yellow-green -> orange -> red (cruise).
 * Ported from WM's ALTITUDE_COLOR_STOPS.
 */
export const ALTITUDE_COLOR_STOPS: Array<{ alt: number; r: number; g: number; b: number }> = [
  { alt: 0,      r: 0,   g: 217, b: 255 },
  { alt: 5000,   r: 50,  g: 250, b: 160 },
  { alt: 10000,  r: 200, g: 230, b: 60  },
  { alt: 20000,  r: 255, g: 165, b: 30  },
  { alt: 30000,  r: 255, g: 100, b: 35  },
  { alt: 40000,  r: 235, g: 50,  b: 55  },
  { alt: 45000,  r: 210, g: 40,  b: 70  },
];

/**
 * Interpolate altitude (in feet) to an RGB color using ALTITUDE_COLOR_STOPS.
 * Ported from WM's altitudeToColor().
 */
export function altitudeToColor(altFt: number): [number, number, number] {
  const stops = ALTITUDE_COLOR_STOPS;
  const alt = Number.isFinite(altFt) ? altFt : 0;
  if (alt <= stops[0]!.alt) return [stops[0]!.r, stops[0]!.g, stops[0]!.b];
  const last = stops[stops.length - 1]!;
  if (alt >= last.alt) return [last.r, last.g, last.b];
  for (let i = 1; i < stops.length; i++) {
    const hi = stops[i]!;
    const lo = stops[i - 1]!;
    if (alt <= hi.alt) {
      const t = (alt - lo.alt) / (hi.alt - lo.alt);
      return [
        Math.round(lo.r + (hi.r - lo.r) * t),
        Math.round(lo.g + (hi.g - lo.g) * t),
        Math.round(lo.b + (hi.b - lo.b) * t),
      ];
    }
  }
  return [last.r, last.g, last.b];
}
